import { useMemo, useRef, useState, useEffect } from "react";
import TotalUsersLineGraph from "./TotalUsersLineGraph";
import { getTotalUsers } from "./data";
import {
  Chart as ChartJs,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import axios from "axios";
import NotificationBadge from "../../components/NotificationBadge";
import { format, formatDistanceToNow, isToday, isAfter, parseISO, subDays, subWeeks, startOfDay, startOfWeek, endOfWeek } from "date-fns";

ChartJs.register(
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LinearScale,
  CategoryScale,
  LineElement,
  PointElement,
  Filler
);

const incomeMonthOptions = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
] as const;

const weekStartsOn: 0 | 1 = 0;

const INCOME_FLOOR_YEAR = 2024;
const INCOME_FLOOR_MONTH_DATE = new Date(2024, 10, 1);

const formatMonthYearLabel = (month: string, year: number) => {
  const m = incomeMonthOptions.find((x) => x.value === month);
  return `${m?.label ?? month} ${year}`;
};

const INCOME_MONTH_API_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

const incomeMonthInternalToApiFilter = (internal: string, calendarNow: Date): string => {
  if (internal === "monthly") return "monthly";
  if (!internal.startsWith("named:")) return "monthly";
  const rest = internal.slice("named:".length);
  const [yStr, mStr] = rest.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return "monthly";
  const curY = calendarNow.getFullYear();
  const curM = calendarNow.getMonth() + 1;
  if (y === curY && m === curM) return "monthly";
  return INCOME_MONTH_API_NAMES[m - 1] ?? "monthly";
};

interface TopSellingItem {
  itemId: string;
  name: string;
  image: string;
  price: number;
}

interface RecentOrder {
  orderId: string;
  orderDate: string;
  location: string;
  status: string;
}

const statusColor: Record<string, string> = {
  delivered: "text-[#008060] bg-[#e6f5ee]",
  cancelled: "text-red-500 bg-red-50",
  "on delivery": "text-yellow-600 bg-yellow-50",
  pending: "text-blue-600 bg-blue-50",
};

const MetricCard = ({
  label,
  value,
  change,
  isPositive,
  icon,
}: {
  label: string;
  value: string;
  change?: string;
  isPositive?: boolean;
  icon?: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col gap-2 min-w-0">
    <div className="flex items-center justify-between">
      <p className="text-gray-500 text-xs sm:text-sm truncate">{label}</p>
      {icon && (
        <span className="flex items-center justify-center p-2 rounded-lg bg-[#6FD8661A]">
          {icon}
        </span>
      )}
    </div>
    <p className="font-bold text-lg sm:text-2xl truncate">{value}</p>
    {change && (
      <p className={`text-xs flex items-center gap-1 ${isPositive ? "text-[#008060]" : "text-red-500"}`}>
        {isPositive
          ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
          : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" /></svg>
        }
        {change} Since Last Week
      </p>
    )}
  </div>
);

const formatNaira = (amount: number): string => {
  return `₦${amount.toLocaleString()}`;
};

const formatCount = (n: number): string => {
  return n.toLocaleString();
};

type IncomeTypeFilter = "all" | "flexible" | "outright";

function coerceNumberArray(v: unknown): number[] | null {
  if (!Array.isArray(v)) return null;
  const nums = v.map((x) => Number(x));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return nums;
}

function defaultLabelsForBucketCount(
  chartPeriod: "Weekly" | "Monthly" | "Yearly" | "All",
  n: number
): string[] {
  if (n <= 0) return [];
  if (chartPeriod === "Weekly") {
    const w = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return Array.from({ length: n }, (_, i) => w[i] ?? `Day ${i + 1}`);
  }
  if (chartPeriod === "Monthly") {
    return Array.from({ length: n }, (_, i) => `Week ${i + 1}`);
  }
  if (chartPeriod === "Yearly") {
    const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return Array.from({ length: n }, (_, i) => m[i] ?? `P${i + 1}`);
  }
  return Array.from({ length: n }, (_, i) => String(i + 1));
}

function tryLabelsAndValues(labels: unknown, values: unknown): { labels: string[]; values: number[] } | null {
  if (!Array.isArray(labels) || !Array.isArray(values) || labels.length === 0 || labels.length !== values.length) {
    return null;
  }
  const nums = coerceNumberArray(values);
  if (!nums) return null;
  return { labels: labels.map((x) => String(x ?? "")), values: nums };
}

function parseSeriesObject(block: Record<string, unknown>): { labels: string[]; values: number[] } | null {
  return (
    tryLabelsAndValues(block.labels ?? block.x, block.data ?? block.y ?? block.values ?? block.amounts) ?? null
  );
}

function parseSeriesBlock(
  block: unknown,
  chartPeriod: "Weekly" | "Monthly" | "Yearly" | "All"
): { labels: string[]; values: number[] } | null {
  if (block == null) return null;
  if (Array.isArray(block)) {
    if (block.length === 0) return null;
    const first = block[0];
    if (typeof first === "number") {
      const nums = coerceNumberArray(block);
      if (!nums) return null;
      return { labels: defaultLabelsForBucketCount(chartPeriod, nums.length), values: nums };
    }
    if (first && typeof first === "object") {
      const rows = block as Record<string, unknown>[];
      const values = rows.map((p) =>
        Number(p.value ?? p.amount ?? p.income ?? p.total ?? p.revenue ?? 0)
      );
      if (values.some((n) => !Number.isFinite(n))) return null;
      const labels = rows.map((p) =>
        String(p.label ?? p.day ?? p.date ?? p.name ?? p.week ?? p.month ?? "")
      );
      const hasLabels = labels.some((l) => l.length > 0);
      return {
        labels: hasLabels ? labels : defaultLabelsForBucketCount(chartPeriod, values.length),
        values,
      };
    }
    return null;
  }
  if (typeof block === "object") {
    return parseSeriesObject(block as Record<string, unknown>);
  }
  return null;
}

function extractIncomeTimeSeriesFromPayload(
  raw: Record<string, unknown> | null,
  incomeType: IncomeTypeFilter,
  chartPeriod: "Weekly" | "Monthly" | "Yearly" | "All"
): { labels: string[]; values: number[] } | null {
  if (!raw) return null;

  const typeBlocks: unknown[] = [];
  if (incomeType === "flexible") {
    typeBlocks.push(
      raw.flexibleSeries,
      raw.flexibleIncomeSeries,
      raw.flexibleChart,
      raw.flexibleBreakdown,
      raw.flexibleDaily,
      raw.incomeSeriesFlexible
    );
  } else if (incomeType === "outright") {
    typeBlocks.push(
      raw.outrightSeries,
      raw.outrightIncomeSeries,
      raw.outrightChart,
      raw.outrightBreakdown,
      raw.outrightDaily,
      raw.incomeSeriesOutright
    );
  }

  const genericBlocks: unknown[] = [
    ...typeBlocks,
    raw.incomeSeries,
    raw.incomeChart,
    raw.series,
    raw.chartSeries,
    raw.timeSeries,
    raw.dailyBreakdown,
    raw.weeklyBreakdown,
    raw.monthlyBreakdown,
    raw.breakdown,
  ];

  for (const b of genericBlocks) {
    const parsed = parseSeriesBlock(b, chartPeriod);
    if (parsed && parsed.values.length > 0) return parsed;
  }

  const numericKeys: [string, "Weekly" | "Monthly" | "Yearly" | "All"][] = [
    ["dailyIncome", "Weekly"],
    ["weeklyIncome", "Monthly"],
    ["monthlyIncome", "Yearly"],
    ["yearlyIncome", "Yearly"],
  ];
  for (const [key, p] of numericKeys) {
    if (chartPeriod !== p && !(key === "yearlyIncome" && chartPeriod === "Yearly")) continue;
    const arr = coerceNumberArray(raw[key]);
    if (arr && arr.length > 0) {
      return { labels: defaultLabelsForBucketCount(chartPeriod, arr.length), values: arr };
    }
  }

  return null;
}

function incomeValuesToCumulativeLine(values: number[], periodTotal: number): number[] {
  if (values.length === 0) return [];
  const v = values.map((n) => Math.max(0, n));
  let inc = 0;
  const perBucket = v.map((n) => (inc += n));
  const lastPer = perBucket[perBucket.length - 1] ?? 0;
  const lastRaw = v[v.length - 1] ?? 0;
  const monotonic = v.every((n, i) => i === 0 || n >= v[i - 1] - 1e-6);
  const matchesTotal =
    periodTotal > 0 && Math.abs(lastRaw - periodTotal) / periodTotal <= 0.08;
  const matchesTotalCum =
    periodTotal > 0 && Math.abs(lastPer - periodTotal) / periodTotal <= 0.08;

  if (monotonic && matchesTotal) return v;
  if (matchesTotalCum) return perBucket;

  const sum = v.reduce((a, b) => a + b, 0);
  if (sum > 0 && periodTotal > 0 && Math.abs(sum - periodTotal) / periodTotal > 0.15) {
    const factor = periodTotal / sum;
    let acc = 0;
    return v.map((n) => (acc += n * factor));
  }
  return perBucket;
}

function buildIncomeLineChartConfig(
  raw: Record<string, unknown> | null,
  chartPeriod: "Weekly" | "Monthly" | "Yearly" | "All",
  incomeType: IncomeTypeFilter,
  periodTotal: number
): { labels: string[]; data: number[] } {
  const series = extractIncomeTimeSeriesFromPayload(raw, incomeType, chartPeriod);
  if (series && series.labels.length === series.values.length && series.values.length > 0) {
    const data = incomeValuesToCumulativeLine(series.values, periodTotal);
    return { labels: series.labels, data };
  }

  if (chartPeriod === "All") {
    return { labels: ["Total"], data: [periodTotal] };
  }
  return { labels: ["Start", "End"], data: [0, periodTotal] };
}

interface DashboardStats {
  totalIncome: number;
  todayIncome: number;
  todayOrders: number;
  totalUsers: number;
}

type NewlyRegisteredUserTypeFilter = "" | "flexible" | "outright";

interface NewlyRegisteredUser {
  id: string;
  name: string;
  email: string;
  createdAt?: string;
  userType?: string;
}

const AdminDashboard = () => {
  const auth = sessionStorage.getItem("authState");
  const authState = auth && JSON.parse(auth);
  const adminLevel = sessionStorage.getItem("adminLevel");
  const isNormalAdmin = adminLevel === "admin";
  const canViewTotalIncome = !isNormalAdmin;
  const [chartPeriod, setChartPeriod] = useState<"Weekly" | "Monthly" | "Yearly" | "All">("Weekly");
  // Keep a stable "now" for UI options; use a fresh Date() inside network effects.
  const now = useMemo(() => new Date(), []);
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    `${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`
  );
  const [incomeYearFilter, setIncomeYearFilter] = useState<"yearly" | string>("yearly");
  const [incomeMonthInternal, setIncomeMonthInternal] = useState<string>("monthly");
  const [incomeWeekScope, setIncomeWeekScope] = useState<"weekly" | "lastweek">("weekly");
  const [incomeType, setIncomeType] = useState<IncomeTypeFilter>("all");
  const [incomeFilterOpen, setIncomeFilterOpen] = useState(false);
  const incomeFilterRef = useRef<HTMLDivElement | null>(null);
  const token = authState?.token || "";
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState<"all" | "today" | "7days" | "month">("all");
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [topSellingItems, setTopSellingItems] = useState<TopSellingItem[]>([]);
  const [topSellingLoading, setTopSellingLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [chartIncomeBreakdown, setChartIncomeBreakdown] = useState<{
    total: number;
    flexible: number;
    outright: number;
  } | null>(null);
  const [incomeApiPayload, setIncomeApiPayload] = useState<Record<string, unknown> | null>(null);
  const [chartIncomeLoading, setChartIncomeLoading] = useState(false);
  const chartIncome = chartIncomeBreakdown
    ? incomeType === "flexible"
      ? chartIncomeBreakdown.flexible
      : incomeType === "outright"
        ? chartIncomeBreakdown.outright
        : chartIncomeBreakdown.total
    : null;
  const [userGrowthData, setUserGrowthData] = useState<any>(null);
  const [userGrowthLoading, setUserGrowthLoading] = useState(true);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [newlyRegisteredUsers, setNewlyRegisteredUsers] = useState<NewlyRegisteredUser[]>([]);
  const [newlyRegisteredLoading, setNewlyRegisteredLoading] = useState(false);
  const [newlyRegisteredError, setNewlyRegisteredError] = useState<string | null>(null);
  const [newlyRegisteredType, setNewlyRegisteredType] = useState<NewlyRegisteredUserTypeFilter>("");

  useEffect(() => {
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getAllEvents`,
          { headers: { Authorization: token } }
        );
        setActivityLog(res.data);
      } catch (error) {
        console.error("Failed to fetch activity log:", error);
      } finally {
        setActivityLoading(false);
      }
    };
    if (token) fetchActivity();
  }, [token]);

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/order-stats`,
          { headers: { Authorization: token } }
        );
        if (res.data?.success) {
          setDashboardStats(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };
    if (token) fetchStats();
  }, [token]);

  useEffect(() => {
    const fetchTopSelling = async () => {
      setTopSellingLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/top-selling-items`,
          { headers: { Authorization: token } }
        );
        if (res.data?.success) {
          setTopSellingItems(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch top selling items:", error);
      } finally {
        setTopSellingLoading(false);
      }
    };
    if (token) fetchTopSelling();
  }, [token]);

  useEffect(() => {
    const fetchRecentOrders = async () => {
      setOrdersLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/recent-orders`,
          { headers: { Authorization: token } }
        );
        if (res.data?.success) {
          setRecentOrders(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch recent orders:", error);
      } finally {
        setOrdersLoading(false);
      }
    };
    if (token) fetchRecentOrders();
  }, [token]);

  useEffect(() => {
    const fetchChartIncome = async () => {
      setChartIncomeLoading(true);
      try {
        const calendarNow = new Date();
        let filter = "weekly";
        if (chartPeriod === "Weekly") {
          filter = incomeWeekScope;
        } else if (chartPeriod === "Monthly") {
          filter = incomeMonthInternalToApiFilter(incomeMonthInternal, calendarNow);
        } else if (chartPeriod === "Yearly") {
          filter = incomeYearFilter === "yearly" ? "yearly" : incomeYearFilter;
        } else {
          filter = "all";
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/total-income`,
          {
            params: { filter },
            headers: { Authorization: token },
          }
        );
        if (res.data?.success) {
          const payload = res.data?.data;
          const obj =
            payload && typeof payload === "object" && !Array.isArray(payload)
              ? (payload as Record<string, unknown>)
              : null;
          setIncomeApiPayload(obj);
          const src = (obj ?? {}) as Record<string, unknown>;
          setChartIncomeBreakdown({
            total: Number(src.totalIncome ?? 0),
            flexible: Number(src.totalIncomeFlexible ?? 0),
            outright: Number(src.totalIncomeOutright ?? 0),
          });
        } else {
          setIncomeApiPayload(null);
          setChartIncomeBreakdown(null);
        }
      } catch (error) {
        console.error("Failed to fetch total income:", error);
        setIncomeApiPayload(null);
      } finally {
        setChartIncomeLoading(false);
      }
    };
    if (token) fetchChartIncome();
  }, [token, chartPeriod, incomeWeekScope, incomeMonthInternal, incomeYearFilter]);

  const monthYearOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    let d = new Date(now.getFullYear(), now.getMonth(), 1);
    while (d >= INCOME_FLOOR_MONTH_DATE) {
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      options.push({ value: `${m}-${y}`, label: formatMonthYearLabel(m, y) });
      d = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    }
    return options;
  }, [now]);

  const currentMonthYearKey = `${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;

  const incomeMonthSelectOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [{ value: "monthly", label: "Current month" }];
    for (const o of monthYearOptions) {
      if (o.value === currentMonthYearKey) continue;
      const [m, y] = o.value.split("-");
      out.push({ value: `named:${y}-${m}`, label: o.label });
    }
    return out;
  }, [monthYearOptions, currentMonthYearKey]);

  const incomeYearFilterOptions = useMemo(() => {
    const cy = now.getFullYear();
    const out: Array<{ value: string; label: string }> = [{ value: "yearly", label: String(cy) }];
    for (let y = cy - 1; y >= INCOME_FLOOR_YEAR; y--) {
      out.push({ value: String(y), label: String(y) });
    }
    return out;
  }, [now]);

  const weeklyRangeLabel = useMemo(() => {
    if (chartPeriod !== "Weekly") return "";
    const anchor = incomeWeekScope === "lastweek" ? subWeeks(now, 1) : now;
    const start = startOfWeek(anchor, { weekStartsOn });
    const end = endOfWeek(anchor, { weekStartsOn });
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }, [chartPeriod, incomeWeekScope, now]);

  const incomeFilterSummary = useMemo(() => {
    const typeSuffix =
      incomeType === "all"
        ? ""
        : ` · ${incomeType === "flexible" ? "Flexible" : "Outright"}`;

    if (chartPeriod === "Weekly") {
      const scopeLabel = incomeWeekScope === "lastweek" ? "Last week" : "This week";
      return `Weekly · ${scopeLabel}${typeSuffix}`;
    }
    if (chartPeriod === "Monthly") {
      if (incomeMonthInternal === "monthly") {
        return `Monthly · Current month${typeSuffix}`;
      }
      const named = incomeMonthSelectOptions.find((o) => o.value === incomeMonthInternal);
      return `Monthly · ${named?.label ?? incomeMonthInternal}${typeSuffix}`;
    }
    if (chartPeriod === "Yearly") {
      const yLabel = incomeYearFilterOptions.find((o) => o.value === incomeYearFilter)?.label ?? incomeYearFilter;
      return `Yearly · ${yLabel}${typeSuffix}`;
    }
    return `All time${typeSuffix}`;
  }, [
    chartPeriod,
    incomeMonthInternal,
    incomeMonthSelectOptions,
    incomeType,
    incomeWeekScope,
    incomeYearFilter,
    incomeYearFilterOptions,
  ]);

  useEffect(() => {
    if (!incomeFilterOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const node = incomeFilterRef.current;
      if (!node) return;
      if (e.target instanceof Node && !node.contains(e.target)) setIncomeFilterOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIncomeFilterOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [incomeFilterOpen]);

  useEffect(() => {
    const fetchUserGrowth = async () => {
      setUserGrowthLoading(true);
      try {
        const [month, year] = selectedMonthYear.split("-");
        const data = await getTotalUsers(year, month, token);
        setUserGrowthData(data);
      } catch (error) {
        console.error("Failed to fetch user growth data:", error);
      } finally {
        setUserGrowthLoading(false);
      }
    };
    if (token) fetchUserGrowth();
  }, [token, selectedMonthYear]);

  useEffect(() => {
    const fetchNewlyRegisteredUsers = async () => {
      setNewlyRegisteredLoading(true);
      setNewlyRegisteredError(null);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/newly-registered-users`,
          { headers: { Authorization: token } }
        );

        const payload = res.data?.data ?? res.data;
        const list: any[] = Array.isArray(payload) ? payload : [];

        const mapped: NewlyRegisteredUser[] = list
          .map((u: any, idx: number) => {
            const id = String(u?._id ?? u?.id ?? u?.userId ?? idx);
            const first = typeof u?.firstName === "string" ? u.firstName : "";
            const last = typeof u?.lastName === "string" ? u.lastName : "";
            const nameFromParts = `${first} ${last}`.trim();
            const name = String(u?.name ?? u?.fullName ?? (nameFromParts || "—"));
            const email = String(u?.email ?? u?.userEmail ?? "—");
            const createdAt = typeof u?.createdAt === "string"
              ? u.createdAt
              : typeof u?.created_at === "string"
                ? u.created_at
                : typeof u?.dateRegistered === "string"
                  ? u.dateRegistered
                  : undefined;
            const userType = typeof u?.type === "string"
              ? u.type
              : typeof u?.userType === "string"
                ? u.userType
                : typeof u?.category === "string"
                  ? u.category
                  : typeof u?.accountType === "string"
                    ? u.accountType
                    : undefined;

            return { id, name, email, createdAt, userType };
          })
          .filter((u: NewlyRegisteredUser) => Boolean(u.id));

        setNewlyRegisteredUsers(mapped);
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch newly registered users";
        setNewlyRegisteredError(String(message));
        setNewlyRegisteredUsers([]);
      } finally {
        setNewlyRegisteredLoading(false);
      }
    };

    if (token) fetchNewlyRegisteredUsers();
  }, [token]);

  const normalizeUserType = (value?: string): NewlyRegisteredUserTypeFilter => {
    const v = (value ?? "").toLowerCase();
    if (v.includes("flex")) return "flexible";
    if (v.includes("out")) return "outright";
    return "";
  };

  const filteredNewlyRegisteredUsers = newlyRegisteredType
    ? newlyRegisteredUsers.filter((u) => normalizeUserType(u.userType) === newlyRegisteredType)
    : newlyRegisteredUsers;

  const incomeChartConfig = useMemo(
    () =>
      buildIncomeLineChartConfig(
        incomeApiPayload,
        chartPeriod,
        incomeType,
        chartIncome ?? 0
      ),
    [incomeApiPayload, chartPeriod, incomeType, chartIncome]
  );

  const incomeData = useMemo(
    () => ({
      labels: incomeChartConfig.labels,
      datasets: [
        {
          label: "Income",
          data: incomeChartConfig.data,
          borderColor: "#008060",
          backgroundColor: (context: any) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, "rgba(0,128,96,0.15)");
            gradient.addColorStop(1, "rgba(0,128,96,0.01)");
            return gradient;
          },
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2,
        },
      ],
    }),
    [incomeChartConfig]
  );

  const incomeOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `₦${context.parsed.y.toLocaleString()}`,
        },
        backgroundColor: "#1f2937",
        titleFont: { size: 12 },
        bodyFont: { size: 13, weight: "bold" },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 }, color: "#9CA3AF" } },
      y: {
        grid: { color: "#f3f4f6" },
        ticks: {
          font: { size: 11 },
          color: "#9CA3AF",
          callback: (v: number) => `₦${v.toLocaleString()}`,
        },
        beginAtZero: true,
      },
    },
  };



  return (
    <main className="w-full">

      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-500 text-xs sm:text-sm">
          Monitor your platform's key metrics and performance
        </p>
      </div>

      <div
        className={`grid grid-cols-2 ${canViewTotalIncome ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3 sm:gap-4 mb-6`}
      >
        {statsLoading ? (
          Array.from({ length: canViewTotalIncome ? 4 : 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col gap-3 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="h-3 bg-gray-200 rounded w-24"></div>
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="h-7 bg-gray-200 rounded w-28"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          ))
        ) : (
          <>
            {canViewTotalIncome ? (
              <MetricCard
                label="Total Income"
                value={formatNaira(dashboardStats?.totalIncome ?? 0)}
                icon={<img src="/assets/naira.svg" alt="icon" className="w-6 h-6" />}
              />
            ) : null}
            <MetricCard
              label="Today's Income"
              value={formatNaira(dashboardStats?.todayIncome ?? 0)}
              icon={<img src="/assets/per-day.svg" alt="icon" className="w-6 h-6" />}
            />
            <MetricCard
              label="Today's Orders"
              value={formatCount(dashboardStats?.todayOrders ?? 0)}
              icon={<img src="/assets/per-day-order.svg" alt="icon" className="w-6 h-6" />}
            />
            <MetricCard
              label="Total Users"
              value={formatCount(dashboardStats?.totalUsers ?? 0)}
              icon={<img src="/assets/total-users.svg" alt="icon" className="w-6 h-6" />}
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">

        <div className="lg:col-span-3 bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
              <div className="min-w-0">
                <h2 className="font-bold text-base sm:text-lg">Total Income</h2>
                {weeklyRangeLabel ? (
                  <p className="text-xs text-gray-400 mt-0.5">{weeklyRangeLabel}</p>
                ) : null}
                {chartIncomeBreakdown && !chartIncomeLoading ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#008060]" />
                      <span className="text-gray-500">Flexible</span>
                      <span className="font-medium text-gray-700">{formatNaira(chartIncomeBreakdown.flexible)}</span>
                    </span>
                    <span className="text-gray-300" aria-hidden="true">·</span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F58634]" />
                      <span className="text-gray-500">Outright</span>
                      <span className="font-medium text-gray-700">{formatNaira(chartIncomeBreakdown.outright)}</span>
                    </span>
                  </div>
                ) : null}
              </div>
              {chartIncomeLoading ? (
                <div className="h-7 w-32 bg-gray-200 rounded animate-pulse"></div>
              ) : chartIncome !== null ? (
                <p className="font-bold text-xl sm:text-2xl text-[#008060]">{formatNaira(chartIncome)}</p>
              ) : null}
              <div className="flex flex-wrap items-center justify-end gap-2">
                <div ref={incomeFilterRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setIncomeFilterOpen((v) => !v)}
                    disabled={chartIncomeLoading}
                    className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 focus:outline-none cursor-pointer disabled:opacity-60"
                    aria-haspopup="menu"
                    aria-expanded={incomeFilterOpen}
                  >
                    <span className="max-w-[220px] sm:max-w-[340px] truncate">{incomeFilterSummary}</span>
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>

                  {incomeFilterOpen ? (
                    <div
                      role="menu"
                      aria-label="Income filters"
                      className="absolute right-0 mt-2 w-[320px] sm:w-[380px] bg-white border border-gray-100 rounded-xl shadow-lg p-3 z-10"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-700">Filters</p>
                        <button
                          type="button"
                          onClick={() => setIncomeFilterOpen(false)}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Close
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-[11px] font-semibold text-gray-500 mb-1">Period</p>
                          <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit flex-wrap gap-0.5">
                            {(
                              [
                                "Weekly",
                                "Monthly",
                                "Yearly",
                                // "All", // total income chart: all-time period hidden from UI
                              ] as const
                            )
                              .filter((p) => !(isNormalAdmin && p === "Yearly"))
                              .map((p) => (
                                <button
                                  key={p}
                                  type="button"
                                  onClick={() => setChartPeriod(p)}
                                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${chartPeriod === p
                                    ? "bg-[#008060] text-white"
                                    : "text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                  {p}
                                </button>
                              ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-[11px] font-semibold text-gray-500 mb-1">Type</p>
                          <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit">
                            {(["all", "flexible", "outright"] as const).map((t) => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setIncomeType(t)}
                                disabled={chartIncomeLoading}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-60 ${incomeType === t
                                  ? "bg-white text-gray-900"
                                  : "text-gray-500 hover:text-gray-700"
                                  }`}
                              >
                                {t === "all" ? "All" : t === "flexible" ? "Flexible" : "Outright"}
                              </button>
                            ))}
                          </div>
                        </div>

                        {chartPeriod === "Weekly" ? (
                          <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit">
                            {(
                              [
                                { value: "weekly" as const, label: "This week" },
                                { value: "lastweek" as const, label: "Last week" },
                              ] as const
                            ).map((o) => (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => setIncomeWeekScope(o.value)}
                                disabled={chartIncomeLoading}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-60 ${incomeWeekScope === o.value
                                  ? "bg-white text-gray-900"
                                  : "text-gray-500 hover:text-gray-700"
                                  }`}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                        ) : chartPeriod === "Monthly" ? (
                          <div className="relative">
                            <select
                              value={incomeMonthInternal}
                              onChange={(e) => setIncomeMonthInternal(e.target.value)}
                              disabled={chartIncomeLoading}
                              className="w-full appearance-none bg-gray-100 rounded-lg pl-3 pr-8 py-2 text-xs font-medium text-gray-700 focus:outline-none cursor-pointer disabled:opacity-60"
                              aria-label="Select month"
                            >
                              {incomeMonthSelectOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                          </div>
                        ) : chartPeriod === "Yearly" ? (
                          <div className="relative">
                            <select
                              value={incomeYearFilter}
                              onChange={(e) => setIncomeYearFilter(e.target.value)}
                              disabled={chartIncomeLoading}
                              className="w-full appearance-none bg-gray-100 rounded-lg pl-3 pr-8 py-2 text-xs font-medium text-gray-700 focus:outline-none cursor-pointer disabled:opacity-60"
                              aria-label="Select year"
                            >
                              {incomeYearFilterOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="h-[220px] sm:h-[260px]">
              <Line data={incomeData} options={incomeOptions} />
            </div>
          </div>

        {/* TOP SELLING ITEM 
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <h2 className="font-bold text-base sm:text-lg mb-1">Top Selling Item</h2>
          <p className="text-gray-400 text-xs mb-4">The top ordered items</p>
          <div className="flex flex-col gap-4">
            {topSellingLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-14"></div>
                </div>
              ))
            ) : topSellingItems.length > 0 ? (
              topSellingItems.map((item) => (
                <div key={item.itemId} className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-orange-50 flex items-center justify-center overflow-hidden flex-shrink-0 p-1.5">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                  </div>
                  <p className="font-medium text-sm flex-1 truncate">{item.name}</p>
                  <p className="font-bold text-sm text-gray-600">₦{item.price.toLocaleString()}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">No items found</p>
            )}
          </div>
        </div>
        */}

        <div className="lg:col-span-2 bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[17px] font-semibold text-gray-800">Newly Registered Users</h2>
            <div className="relative">
              <select
                value={newlyRegisteredType}
                onChange={(e) => setNewlyRegisteredType(e.target.value as NewlyRegisteredUserTypeFilter)}
                className="appearance-none bg-gray-100/80 border-none text-gray-600 text-xs rounded-md pl-3 pr-8 py-1.5 focus:outline-none cursor-pointer"
              >
                <option value="">Type</option>
                <option value="flexible">Flexible</option>
                <option value="outright">Outright</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>
          <p className="text-[#008060] text-sm mb-4">Newly Registered Users</p>
          
          <div
            className="flex flex-col gap-4 min-h-0 max-h-[220px] sm:max-h-[260px] overflow-y-auto pr-1 sm:pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            style={{ scrollbarWidth: "none" }}
          >
            {newlyRegisteredLoading ? (
              <p className="text-sm text-gray-400 text-center py-4">Loading newly registered users...</p>
            ) : newlyRegisteredError ? (
              <p className="text-sm text-red-500 text-center py-4">{newlyRegisteredError}</p>
            ) : filteredNewlyRegisteredUsers.length > 0 ? (
              filteredNewlyRegisteredUsers.map((user, idx, arr) => {
                const normalizedType = normalizeUserType(user.userType);
                const typeLabel = normalizedType === "flexible"
                  ? "Flexible User"
                  : normalizedType === "outright"
                    ? "Outright User"
                    : "User";
                const typeColor = normalizedType === "outright" ? "text-[#F58634]" : "text-[#008060]";
                const time = user.createdAt
                  ? formatDistanceToNow(parseISO(user.createdAt), { addSuffix: true })
                  : "";

                return (
                  <div key={user.id} className={`flex items-start justify-between ${idx !== arr.length - 1 ? 'border-b border-gray-100 pb-4' : ''}`}>
                    <div className="flex flex-col gap-1">
                      <p className="text-[15px] text-gray-600">
                        <span className="font-semibold text-black">{user.name}</span> joined as new user
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-400">
                        <span>{user.email}</span>
                        {time ? <span>{time}</span> : null}
                      </div>
                    </div>
                    <span className={`text-[13px] mt-0.5 ${typeColor}`}>{typeLabel}</span>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">No newly registered users</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

        <TotalUsersLineGraph
          isLoading={userGrowthLoading}
          data={userGrowthData}
          selectedMonthYear={selectedMonthYear}
          setSelectedMonthYear={setSelectedMonthYear}
        />

        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-base sm:text-lg">Last Orders</h2>
            <button
              onClick={() => setShowAllOrders(true)}
              className="text-xs text-[#008060] font-medium hover:underline outline-none"
            >
              View All
            </button>
          </div>
          {ordersLoading ? (
            <div className="flex flex-col gap-3 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-3 bg-gray-200 rounded w-14"></div>
                  <div className="h-3 bg-gray-200 rounded flex-1"></div>
                  <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                </div>
              ))}
            </div>
          ) : recentOrders.length > 0 ? (
            <div className="max-h-[350px] overflow-y-auto" style={{ scrollbarWidth: 'none' }}>

              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-100">
                      <th className="text-left py-2 font-medium">Order ID</th>
                      <th className="text-left py-2 font-medium">O.Time</th>
                      <th className="text-left py-2 font-medium">Location</th>
                      <th className="text-left py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.orderId} className="border-b border-gray-50">
                        <td className="py-3 font-medium text-gray-800">#{order.orderId.slice(-4)}</td>
                        <td className="py-3 text-gray-500">{format(parseISO(order.orderDate), "h:mm a")}</td>
                        <td className="py-3 text-gray-500 max-w-[200px] truncate">{order.location}</td>
                        <td className="py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColor[order.status.toLowerCase()] || "text-gray-600 bg-gray-100"}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sm:hidden flex flex-col gap-3">
                {recentOrders.map((order) => (
                  <div key={order.orderId} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-gray-800">#{order.orderId.slice(-4)}</p>
                      <p className="text-xs text-gray-400">{format(parseISO(order.orderDate), "h:mm a")} · {order.location}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColor[order.status.toLowerCase()] || "text-gray-600 bg-gray-100"}`}>
                      {order.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No recent orders</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-base sm:text-lg">Real-time Activity</h2>
            <p className="text-gray-400 text-xs">Latest platform activities</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#008060] font-medium">Live</span>
            <img src="/assets/fluent_location-live-20-regular.svg" alt="live" className="w-5 h-5" />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          {(["all", "today", "7days", "month"] as const).map((f) => {
            const labels: Record<string, string> = { all: "All", today: "Today", "7days": "Last 7 Days", month: "Last Month" };
            return (
              <button
                key={f}
                onClick={() => setActivityFilter(f)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${activityFilter === f
                  ? "bg-[#008060] text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
              >
                {labels[f]}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {activityLoading ? (
            <p className="text-sm text-gray-400 text-center py-4">Loading activities...</p>
          ) : (() => {
            const filtered = activityLog
              .slice()
              .reverse()
              .filter((a: any) => {
                if (activityFilter === "all") return true;
                if (!a.createdAt) return false;
                const date = parseISO(a.createdAt);
                if (activityFilter === "today") return isToday(date);
                if (activityFilter === "7days") return isAfter(date, startOfDay(subDays(new Date(), 7)));
                if (activityFilter === "month") return isAfter(date, startOfDay(subDays(new Date(), 30)));
                return true;
              });

            return filtered.length > 0 ? (
              filtered.slice(0, 20).map((activity: any, idx: number) => (
                <div key={idx} className="flex items-center gap-3 py-3 pl-4 border-b border-gray-50 last:border-b-0">
                  <span className="text-gray-400 text-sm">—</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 capitalize">
                      <span className="font-semibold">{activity.message?.split(" ")[0]} {activity.message?.split(" ")[1]}</span>
                      {" "}{activity.message?.split(" ").slice(2).join(" ")}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {activity.createdAt
                        ? formatDistanceToNow(parseISO(activity.createdAt), { addSuffix: true })
                        : ""}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                    {activity.createdAt
                      ? format(parseISO(activity.createdAt), "h:mm a")
                      : ""}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">No activities found</p>
            );
          })()}
        </div>
      </div>

      {showAllOrders && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900">All Recent Orders</h3>
                <p className="text-sm text-gray-500 mt-1">Showing {recentOrders.length} orders</p>
              </div>
              <button
                onClick={() => setShowAllOrders(false)}
                className="p-2 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-5" style={{ scrollbarWidth: 'none' }}>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-100 bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium rounded-l-lg">Order ID</th>
                      <th className="text-left py-3 px-4 font-medium">Order Time</th>
                      <th className="text-left py-3 px-4 font-medium">Location</th>
                      <th className="text-left py-3 px-4 font-medium rounded-r-lg">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.orderId} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-4 font-bold text-gray-800">#{order.orderId.slice(-4)}</td>
                        <td className="py-4 px-4 text-gray-500 font-medium">{format(parseISO(order.orderDate), "MMM d, h:mm a")}</td>
                        <td className="py-4 px-4 text-gray-600">{order.location}</td>
                        <td className="py-4 px-4">
                          <span className={`text-xs font-bold px-3 py-1.5 rounded-full capitalize ${statusColor[order.status.toLowerCase()] || "text-gray-600 bg-gray-100"}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="sm:hidden flex flex-col gap-3">
                {recentOrders.map((order) => (
                  <div key={order.orderId} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between border border-gray-100">
                    <div>
                      <p className="font-bold text-sm text-gray-800">#{order.orderId.slice(-4)}</p>
                      <p className="text-xs text-gray-500 mt-1">{format(parseISO(order.orderDate), "MMM d, h:mm a")}</p>
                      <p className="text-xs text-gray-600 font-medium mt-0.5">{order.location}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full capitalize ${statusColor[order.status.toLowerCase()] || "text-gray-600 bg-gray-100"}`}>
                      {order.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default AdminDashboard;
